import { useMemo } from 'react';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuBadge,
  SheetHeader,
  SheetTitle,
  Button,
} from '@predoc/cow';
import { Download, Loader2, RefreshCw, ThumbsUp } from 'lucide-react';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { DownloadItem } from './DownloadItem';
import { type DownloadItemType } from './types';
import { Permission, useHasPermission } from '@/utils/roles';
import {
  useListDownloads,
  useClearUserDownloads,
  useMarkDownloadItemAsRead,
  useMarkAllDownloadItemsAsRead,
  useDownloadsUpdates,
} from './hooks/downloadsAPI';

export const DownloadsSheet = () => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListDownloads();

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const pageCount = data?.pages.length ?? 0;

  const unreadCount = data?.pages[0]?.countUnread ?? 0;

  const showThatsAllIndicator =
    pageCount > 1 && !hasNextPage && items.length > 0 && !isFetchingNextPage;

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const { hasUpdates, dismissUpdates } = useDownloadsUpdates();
  const { mutate: clearDownloads } = useClearUserDownloads();
  const { mutate: markItemAsRead } = useMarkDownloadItemAsRead();
  const { mutate: markAllAsRead } = useMarkAllDownloadItemsAsRead();

  const handleClearHistory = () => {
    clearDownloads();
  };

  const handleMarkAsRead = (
    id: string,
    patientId: string,
    itemType: DownloadItemType,
  ) => {
    markItemAsRead({ documentId: id, patientId, itemType });
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleDownloadsSheetOpenAutoFocus = (event: Event) => {
    event.preventDefault();
  };

  return (
    <SheetContent
      side="left"
      className="flex flex-col p-0"
      onOpenAutoFocus={handleDownloadsSheetOpenAutoFocus}
    >
      <SheetHeader className="shrink-0 border-b px-4 py-4">
        <SheetTitle>
          Downloads{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </SheetTitle>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto" role="list">
        {hasUpdates && (
          <Button
            variant="ghost"
            onClick={dismissUpdates}
            className="bg-primary/10 text-primary hover:bg-primary/15 flex w-full items-center justify-center gap-2 rounded-none px-4 py-2 text-sm font-medium"
            aria-label="New updates available, click to refresh"
          >
            <RefreshCw className="h-4 w-4" />
            New updates available — click to refresh
          </Button>
        )}
        {items.length === 0 && !isFetchingNextPage ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6">
            <img
              src="/suggested-providers.png"
              alt="No downloads"
              className="mb-2 w-56"
            />
            <h3 className="text-lg font-bold">No Downloads</h3>
            <p className="text-muted-foreground text-sm">
              Downloaded patient data will appear here.
            </p>
          </div>
        ) : (
          <InfiniteScroll
            isLoading={isFetchingNextPage}
            hasMore={hasNextPage ?? false}
            next={handleLoadMore}
            threshold={0.8}
          >
            {items.map((item) => (
              <DownloadItem
                key={item.id}
                item={item}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
            <div className="flex items-center justify-center gap-2 py-4">
              {isFetchingNextPage && (
                <>
                  <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                  <span className="text-muted-foreground text-sm">
                    Loading more..
                  </span>
                </>
              )}
              {showThatsAllIndicator && (
                <>
                  <ThumbsUp className="text-muted-foreground h-6 w-6" />
                  <span className="text-muted-foreground text-sm font-medium">
                    That&apos;s all.
                  </span>
                </>
              )}
            </div>
          </InfiniteScroll>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between border-t px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearHistory}
          aria-label="Clear download history"
          disabled={items.length === 0}
        >
          Clear Download History
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMarkAllAsRead}
          aria-label="Mark all downloads as read"
          disabled={unreadCount === 0}
        >
          Mark All as Read
        </Button>
      </div>
    </SheetContent>
  );
};

export const DownloadsNavItem = () => {
  const { hasPermission: canViewDownloads } = useHasPermission(
    Permission.VIEW_DOWNLOADS,
  );
  const { data } = useListDownloads(canViewDownloads);
  const unreadCount = data?.pages[0]?.countUnread ?? 0;

  if (!canViewDownloads) {
    return null;
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="w-full cursor-pointer">
            <Download className="h-5 w-5" />
            <span>Downloads</span>
          </SidebarMenuButton>
          {unreadCount > 0 && (
            <SidebarMenuBadge aria-label={`${unreadCount} unread downloads`}>
              {unreadCount}
            </SidebarMenuBadge>
          )}
        </SidebarMenuItem>
      </SheetTrigger>
      <DownloadsSheet />
    </Sheet>
  );
};
